import React, { createContext, useContext, useEffect, useState } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useUser } from '../UserContext';

const NotificationContext = createContext({
  notifications: [],
  setNotifications: () => {}
});

export const NotificationProvider = ({ children }) => {
  const { user } = useUser();
  const [notifications, setNotifications] = useState([]);

  // Helper: format "YYYY-MM-DD ..." → "DD/MM/YYYY"
  const extractDateOnly = (rawTs) => {
    if (!rawTs) return '';
    const s = rawTs.toString().trim().slice(0, 10); // e.g. "2025-05-31"
    const parts = s.split('-');
    if (
      parts.length === 3 &&
      parts[0].length === 4 &&
      parts[1].length === 2 &&
      parts[2].length === 2 &&
      !isNaN(Number(parts[0])) &&
      !isNaN(Number(parts[1])) &&
      !isNaN(Number(parts[2]))
    ) {
      return `${parts[2]}/${parts[1]}/${parts[0]}`; // "DD/MM/YYYY"
    }
    return s;
  };

  useEffect(() => {
    if (!user?.id) return;
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';

    // 1) Load historic notifications
    axios
      .get(`${apiUrl}/api/notifications`, {
        params: { userId: user.id },
        withCredentials: true
      })
      .then((res) => {
        const normalized = res.data.map((n) => {
          let parsedPayload;
          try {
            parsedPayload = JSON.parse(n.payload);
          } catch {
            parsedPayload = n.payload;
          }
          return {
            id: n.id,
            type: n.type,
            payload: parsedPayload,
            isRead: Boolean(n.isRead),             // ensure boolean
            createdAt: extractDateOnly(n.createdAt) // "DD/MM/YYYY"
          };
        });
        console.log('🏷️ Fetched notifications:', normalized);
        setNotifications(normalized);
      })
      .catch((err) => {
        console.error('Error loading notifications:', err);
      });

    // 2) Open a WebSocket-only connection
    const socket = io(apiUrl, {
      query: { userId: user.id },
      transports: ['websocket'],
      withCredentials: true
    });

    // 3) Listen for live pushes
    socket.on('notification', (notif) => {
      let parsedPayload;
      try {
        parsedPayload = JSON.parse(notif.payload);
      } catch {
        parsedPayload = notif.payload;
      }
      const normalizedPush = {
        id: notif.id,
        type: notif.type,
        payload: parsedPayload,
        isRead: Boolean(notif.isRead),
        createdAt: extractDateOnly(notif.createdAt)
      };
      console.log('🔔 Real‐time push:', normalizedPush);
      setNotifications((prev) => [normalizedPush, ...prev]);
      toast.info(parsedPayload.message, {
        position: 'top-right',
        autoClose: 5000
      });
    });

    return () => {
      socket.disconnect();
    };
  }, [user]);

  return (
    <NotificationContext.Provider value={{ notifications, setNotifications }}>
      {children}
      <ToastContainer position="top-right" autoClose={5000} />
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return ctx; // { notifications, setNotifications }
};
